import React, { useRef, useEffect } from 'react'
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline'

const ANYWHERE = 'Anywhere'

const formatLocationList = locations => {
	if (!Array.isArray(locations) || locations.length === 0) return null
	const first = locations[0]?.name
	if (!first) return null
	const rest = locations.length - 1
	return rest > 0 ? `${first} +${rest}` : first
}

const formatCityState = (city, state) => {
	if (!city) return null
	return state ? `${city}, ${state}` : city
}

const titleCase = s =>
	typeof s === 'string' && s.length
		? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
		: s

const formatList = (list, label, transform = v => v) => {
	if (!Array.isArray(list) || list.length === 0) return null
	const head = transform(list[0])
	if (list.length === 1) return label ? `${label}: ${head}` : head
	return `${head} +${list.length - 1}`
}

const deriveTitle = filters => {
	if (!filters) return ANYWHERE
	return (
		formatLocationList(filters.pickupLocations) ||
		formatCityState(filters.pickupCity, filters.pickupState) ||
		ANYWHERE
	)
}

const deriveSubtitle = filters => {
	if (!filters) return ANYWHERE
	return (
		formatLocationList(filters.dropoffLocations) ||
		formatCityState(filters.dropCity, filters.dropState) ||
		formatList(filters.destinationStates, '') ||
		formatList(filters.destinationRegions, '', titleCase) ||
		ANYWHERE
	)
}

const shortModeLabel = label => label.replace(/\s*mode$/i, '')

function BoardModeTabs({ value, options, onChange, isDarkMode, heightClass }) {
	const trackClasses = isDarkMode
		? 'bg-white/[0.05] ring-1 ring-white/10 shadow-inner'
		: 'bg-gray-100 ring-1 ring-gray-200'

	const activeClasses = isDarkMode
		? 'bg-sky-500 text-white shadow-sm'
		: 'bg-white text-sky-600 shadow-sm'

	const inactiveClasses = isDarkMode
		? 'text-gray-400 hover:text-gray-100'
		: 'text-gray-500 hover:text-gray-900'
	const disabledClasses = isDarkMode
		? 'cursor-not-allowed text-gray-400'
		: 'cursor-not-allowed text-gray-500'

	return (
		<div className={`flex items-center shrink-0 ${heightClass}`}>
			<div
				role="tablist"
				aria-label="Load board mode"
				className={`flex items-center h-full p-[3px] gap-0.5 rounded-lg ${trackClasses}`}
			>
				{options.map(opt => {
					const isSelected = opt.value === value
					const isDisabled = !!opt.disabled
					return (
						<button
							key={opt.value}
							type="button"
							role="tab"
							aria-selected={isSelected}
							disabled={isDisabled}
							onClick={() => {
								if (isDisabled) return
								if (opt.href) {
									window.location.href = opt.href
									return
								}
								onChange?.(opt.value)
							}}
							className={`flex items-center gap-1 h-full px-3 rounded-md text-[11px] font-semibold leading-none transition-colors duration-150 ${
								isDisabled
									? disabledClasses
									: isSelected
									? activeClasses
									: `cursor-pointer ${inactiveClasses}`
							}`}
						>
							{shortModeLabel(opt.label)}
						</button>
					)
				})}
			</div>
		</div>
	)
}

function SearchTabs({
	tabs,
	activeTabId,
	onSelect,
	onClose,
	onAdd,
	boardMode,
	onBoardModeChange,
	boardOptions,
	showBoardModeTabs = false,
	hideSearchTabs = false,
	isDarkMode,
}) {
	const SIDE_CONTROL_HEIGHT = 'h-8'

	// Scroll the active tab into view within the track — when a new tab is added
	// (and becomes active) past the visible edge, slide to it so it's seen.
	const trackRef = useRef(null)
	useEffect(() => {
		const track = trackRef.current
		if (!track) return
		// Defer a frame so the newly added tab's flex layout is settled before
		// we measure and scroll it into view.
		const id = requestAnimationFrame(() => {
			const activeEl = track.querySelector('[data-active="true"]')
			if (!activeEl) return
			const t = track.getBoundingClientRect()
			const e = activeEl.getBoundingClientRect()
			if (e.right > t.right) {
				track.scrollTo({ left: track.scrollLeft + (e.right - t.right) + 8, behavior: 'smooth' })
			} else if (e.left < t.left) {
				track.scrollTo({ left: track.scrollLeft - (t.left - e.left) - 8, behavior: 'smooth' })
			}
		})
		return () => cancelAnimationFrame(id)
	}, [activeTabId, tabs.length])

	// Segmented control: tabs sit in one shared track, the active tab is a
	// raised "tablet". Borderless; transition gives a subtle slide/fade.
	const tabBaseClasses = `group relative flex items-center gap-2 max-w-[240px] px-2.5 h-8 rounded-lg text-left transition-all duration-200 ease-out shrink-0`

	const tabActiveClasses = isDarkMode
		? 'bg-white/[0.12] text-white shadow-sm'
		: 'bg-white text-gray-900 shadow-sm'

	const tabInactiveClasses = isDarkMode
		? 'text-gray-400 hover:text-gray-200'
		: 'text-gray-500 hover:text-gray-800'

	const subtitleActiveClasses = isDarkMode ? 'text-gray-400' : 'text-gray-500'
	const subtitleInactiveClasses = isDarkMode ? 'text-gray-500' : 'text-gray-400'

	const tabsTrackClasses = isDarkMode ? 'bg-white/[0.05]' : 'bg-gray-100'

	const closeBtnClasses = isDarkMode
		? 'hover:bg-white/15 text-gray-300 hover:text-white'
		: 'hover:bg-gray-200 text-gray-500 hover:text-gray-900'

	const addBtnClasses =
		'bg-[#1F6FEB] text-white hover:bg-[#1b63d1]'

	return (
		<div className="flex items-center gap-2 min-w-0 flex-1">
			<style>{`
				.lf-tabs-scroll::-webkit-scrollbar { display: none; }
				.lf-tabs-scroll { scrollbar-width: none; -ms-overflow-style: none; }
			`}</style>

			{showBoardModeTabs && (
				<>
					<BoardModeTabs
						value={boardMode}
						options={boardOptions}
						onChange={onBoardModeChange}
						isDarkMode={isDarkMode}
						heightClass={SIDE_CONTROL_HEIGHT}
					/>

					{/* Tabs + add are a desktop affordance. On mobile we hide the strip,
					    divider, and + and just run the active tab's search. */}
					{!hideSearchTabs && (
						<span
							className={`hidden md:block shrink-0 w-px h-5 ${isDarkMode ? 'bg-white/30' : 'bg-gray-300'}`}
							aria-hidden="true"
						/>
					)}
				</>
			)}

			{!hideSearchTabs && (
			<div
				ref={trackRef}
				className={`lf-tabs-scroll hidden md:flex items-center gap-0.5 overflow-x-auto min-w-0 p-1 rounded-xl ${tabsTrackClasses}`}
			>
				{tabs.map(tab => {
					const isActive = tab.id === activeTabId
					const title = deriveTitle(tab.filters)
					const subtitle = deriveSubtitle(tab.filters)
					return (
						<button
							key={tab.id}
							type="button"
							data-active={isActive ? 'true' : undefined}
							onClick={() => onSelect(tab.id)}
							className={`${tabBaseClasses} ${isActive ? tabActiveClasses : tabInactiveClasses}`}
						>
							<div className="flex items-center gap-1 min-w-0">
								<span className="shrink-0 max-w-[130px] text-[11px] font-semibold leading-[1.15] truncate">
									{title}
								</span>
								<span
									className={`shrink-0 text-[10px] ${
										isActive ? subtitleActiveClasses : subtitleInactiveClasses
									}`}
									aria-hidden="true"
								>
									→
								</span>
								<span
									className={`min-w-0 text-[11px] leading-[1.15] truncate ${
										isActive ? subtitleActiveClasses : subtitleInactiveClasses
									}`}
								>
									{subtitle}
								</span>
							</div>

							<span
								role="button"
								tabIndex={0}
								onClick={e => {
									e.stopPropagation()
									onClose(tab.id)
								}}
								onKeyDown={e => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.stopPropagation()
										e.preventDefault()
										onClose(tab.id)
									}
								}}
								className={`ml-auto -mr-0.5 p-0.5 rounded ${closeBtnClasses} ${
									isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
								}`}
								aria-label="Close search tab"
							>
								<XMarkIcon className="h-3.5 w-3.5" />
							</span>
						</button>
					)
				})}
			</div>
			)}

			{!hideSearchTabs && (
				<button
					type="button"
					onClick={onAdd}
					className={`hidden md:flex items-center justify-center w-8 ${SIDE_CONTROL_HEIGHT} rounded-md shrink-0 transition-colors ${addBtnClasses}`}
					aria-label="Add search tab"
				>
					<PlusIcon className="h-5 w-5 stroke-[3.5]" />
				</button>
			)}
		</div>
	)
}

export default SearchTabs
